import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import ReactMapboxGl, {Layer, Source} from "react-mapbox-gl";
import mapboxgl, {GeoJSONSourceRaw, Map as MapBoxMap} from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import {MapEvent} from "react-mapbox-gl/lib/map-events";
import {Box, CircularProgress} from "@material-ui/core";
import {bbox} from "@turf/turf"
import {MapScaleControl} from "../MapScaleControl";

type TransformRequestFn = (url: string, resourceType: string) => { url: string; headers?: Record<string, string> };

const DefaultMap = ReactMapboxGl({
  //@ts-ignore
  accessToken: window._env_.API_MAPBOX_ACCESS_TOKEN,
  attributionControl: false,
});

const mapStartProps: {
  containerStyle: object;
  center: [number, number];
  zoom: [number];
} = {
  containerStyle: { width: "100%", minHeight: 600 },
  center: [6, 18],
  zoom: [1],
};

export const EMPTY_GEO_JSON = `{
  "type": "Feature",
  "properties": {},
  "geometry": {
      "type": "Polygon",
      "coordinates": [
      ]
  }
}
`;

export interface GeoZoneMapPointsLayer {
  geoJson: string
  color: string
}

export interface HeatmapLayer {
  geoJson: string;
  color?: string;  // Base color for the heatmap
}

export interface VectorTileLayerConfig {
  sourceId: string;
  tileUrl: string;
  layers: Array<{
    sourceLayer: string;
    type: "circle" | "line" | "fill" | "symbol";
    paint: Record<string, any>;
    filter?: any[];
  }>;
  minzoom?: number;
  maxzoom?: number;
}

export interface GeoZoneMapProps {
  areaGeoJson: string;
  pointsLayers?: GeoZoneMapPointsLayer[];
  heatmapLayers?: HeatmapLayer[];
  vectorTileLayers?: VectorTileLayerConfig[];
  mapContainerClassName?: string;
  onMoveEnd?: (map: MapBoxMap) => void;
  initialZoom?: number;
  transformRequest?: TransformRequestFn;
}

export const GeoZoneMap = (props: GeoZoneMapProps) => {
  const { areaGeoJson, pointsLayers, heatmapLayers, vectorTileLayers, mapContainerClassName, onMoveEnd, initialZoom, transformRequest } = props;

  const [map, setmap] = useState<MapBoxMap | undefined>(undefined);
  const [zoom, setZoom] = useState<[number]>(initialZoom ? [initialZoom] : mapStartProps.zoom);
  const [tilesLoading, setTilesLoading] = useState(false);

  // Use a custom Map factory when transformRequest is provided (for auth headers on tile requests)
  const MapComponent = useRef(
    transformRequest
      ? ReactMapboxGl({
          //@ts-ignore
          accessToken: window._env_.API_MAPBOX_ACCESS_TOKEN,
          attributionControl: false,
          transformRequest,
        })
      : DefaultMap
  ).current;

  const onStyleLoad = useCallback<MapEvent>((map: MapBoxMap) => {
    setmap(map);
  }, []);

  const handleMoveEnd = useCallback<MapEvent>((map: MapBoxMap) => {
    setZoom([map.getZoom()]);
    onMoveEnd?.(map);
  }, [onMoveEnd]);

  const currentGeoJson = useMemo(() => {
    try {
      return areaGeoJson
      ? JSON.parse(areaGeoJson)
      : JSON.parse(EMPTY_GEO_JSON)
    } catch {
      return JSON.parse(EMPTY_GEO_JSON)
    }
  }, [areaGeoJson])

  const highlightedArea: GeoJSONSourceRaw = useMemo(
    () => ({
      data: currentGeoJson,
      type: "geojson",
    }),
    [currentGeoJson]
  );

  useEffect(() => {
    try {
      const bounds = bbox(currentGeoJson)
      // @ts-ignore
      map?.fitBounds(bounds, {padding: 20})
    } catch {}
  }, [currentGeoJson, map])

  // Track vector tile loading state
  const vectorTileSourceIds = useMemo(
    () => vectorTileLayers?.map((vtl) => vtl.sourceId) ?? [],
    [vectorTileLayers]
  );

  useEffect(() => {
    if (!map || vectorTileSourceIds.length === 0) {
      setTilesLoading(false);
      return;
    }

    const onSourceDataLoading = (e: any) => {
      if (vectorTileSourceIds.includes(e.sourceId)) {
        setTilesLoading(true);
      }
    };
    const onIdle = () => {
      setTilesLoading(false);
    };

    map.on("sourcedataloading", onSourceDataLoading);
    map.on("idle", onIdle);
    return () => {
      map.off("sourcedataloading", onSourceDataLoading);
      map.off("idle", onIdle);
    };
  }, [map, vectorTileSourceIds]);

  const additionalLayers = useMemo(() => {
    if (!pointsLayers) return null

    return pointsLayers.map((layer, index) => {
      const sourceId = `points_${index}`
      return <>
        <Source
          id={sourceId}
          geoJsonSource={{
            data: JSON.parse(layer.geoJson),
            type: "geojson",
          }}
        />
        <Layer
          type="circle"
          sourceId={sourceId}
          paint={{ "circle-radius": 6, "circle-color": layer.color }}
        />
      </>
    })
  }, [pointsLayers])

  const heatmapLayersRendered = useMemo(() => {
    if (!heatmapLayers) return null

    const heatmapGradients: Record<string, any[]> = {
      blue: [
        "interpolate", ["linear"], ["heatmap-density"],
        0, "rgba(59,130,246,0)",
        0.2, "rgb(190,210,255)",
        0.4, "rgb(120,170,255)",
        0.6, "rgb(59,130,246)",
        0.8, "rgb(30,80,200)",
        1, "rgb(10,40,140)"
      ],
      orange: [
        "interpolate", ["linear"], ["heatmap-density"],
        0, "rgba(255,102,0,0)",
        0.2, "rgb(255,200,150)",
        0.4, "rgb(255,160,80)",
        0.6, "rgb(255,102,0)",
        0.8, "rgb(220,80,0)",
        1, "rgb(180,50,0)"
      ],
      yellow: [
        "interpolate", ["linear"], ["heatmap-density"],
        0, "rgba(234,179,8,0)",
        0.2, "rgb(234,179,8)",
        0.6, "rgb(255,200,50)",
        1, "rgb(255,255,150)"
      ],
    };

    return heatmapLayers.map((layer, index) => {
      const sourceId = `heatmap_${index}`
      const gradient = heatmapGradients[layer.color ?? "blue"] ?? heatmapGradients.blue;

      return <>
        <Source
          id={sourceId}
          geoJsonSource={{
            data: JSON.parse(layer.geoJson),
            type: "geojson",
          }}
        />
        <Layer
          id={`heatmap_layer_${index}`}
          type="heatmap"
          sourceId={sourceId}
          paint={{
            // Weight based on point count
            "heatmap-weight": ["get", "weight"],
            // Intensity increases with zoom
            "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 0, 1, 12, 3],
            // Color gradient
            "heatmap-color": gradient,
            // Radius increases with zoom
            "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 0, 2, 13, 20],
            // Fade out as we approach zoom 13 (vector tiles start at 13)
            "heatmap-opacity": ["interpolate", ["linear"], ["zoom"], 11, 1, 13, 0]
          }}
        />
      </>
    })
  }, [heatmapLayers])

  const vectorTileLayersRendered = useMemo(() => {
    if (!vectorTileLayers) return null

    return vectorTileLayers.map((vtl) => (
      <React.Fragment key={vtl.tileUrl}>
        <Source
          key={vtl.tileUrl}
          id={vtl.sourceId}
          tileJsonSource={{
            type: "vector",
            tiles: [vtl.tileUrl],
            minzoom: vtl.minzoom,
            maxzoom: vtl.maxzoom,
          }}
        />
        {vtl.layers.map((layer, idx) => (
          <Layer
            key={`${vtl.sourceId}_${layer.sourceLayer}_${idx}`}
            id={`${vtl.sourceId}_${layer.sourceLayer}_${idx}`}
            type={layer.type}
            sourceId={vtl.sourceId}
            sourceLayer={layer.sourceLayer}
            paint={layer.paint}
            filter={layer.filter}
          />
        ))}
      </React.Fragment>
    ))
  }, [vectorTileLayers])

  if (!mapboxgl.supported()) {
    return <Box className={mapContainerClassName}>
        Votre navigateur ne supporte pas la visualisation de carte.
    </Box>
  }

  return (
    <>
      <Box className={mapContainerClassName} style={{ position: "relative" }}>
        {tilesLoading && (
          <Box style={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 1,
            backgroundColor: "rgba(255,255,255,0.85)",
            borderRadius: 4,
            padding: 6,
            display: "flex",
            alignItems: "center",
          }}>
            <CircularProgress size={18} />
          </Box>
        )}
        <MapComponent
          style="mapbox://styles/mapbox/outdoors-v11"
          containerStyle={mapStartProps.containerStyle}
          center={mapStartProps.center}
          zoom={zoom}
          onStyleLoad={onStyleLoad}
          onMoveEnd={handleMoveEnd}
        >
          <Source id="highlightedArea" geoJsonSource={highlightedArea} />
          <Layer
            type="fill"
            sourceId="highlightedArea"
            paint={{ "fill-color": "#ff0000", "fill-opacity": 0.3 }}
          />
          <Layer
            type="line"
            sourceId="highlightedArea"
            paint={{ "line-color": "#ff0000", "line-width": 1 }}
          />
          {heatmapLayersRendered}
          {vectorTileLayersRendered}
          {additionalLayers}
          <MapScaleControl />
        </MapComponent>
      </Box>
    </>
  );
};
